<template>
  <div class="app-home">
    <section class="app-home__hero">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <div class="app-home__hero-text">
        <p v-if="greeting" class="app-home__greeting">{{ greeting }}</p>
        <h1 class="app-home__title">{{ welcomeTitle }}</h1>
        <p v-if="welcomeText" class="app-home__subtitle">{{ welcomeText }}</p>
      </div>
    </section>

    <div v-if="visibleTiles.length" class="app-home__grid">
      <button
        v-for="tile in visibleTiles"
        :key="tile.route"
        type="button"
        class="app-home__tile"
        @click="onTileClick(tile)"
      >
        <span class="app-home__tile-icon"><v-icon size="20">{{ tile.icon }}</v-icon></span>
        <span class="app-home__tile-title">{{ tile.title }}</span>
        <span v-if="tile.description" class="app-home__tile-desc">{{ tile.description }}</span>
        <v-icon size="16" class="app-home__tile-go">arrow_forward</v-icon>
      </button>
    </div>
    <div v-else class="app-home__empty-state">
      <v-icon size="26" class="app-home__empty-icon">apps</v-icon>
      <p class="app-home__empty">{{ t("appHome.empty") }}</p>
      <p class="app-home__empty-hint">{{ t("appHome.emptyHint", { app: tenantName }) }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from "vue";
import { useI18n } from "vue-i18n";
import { navigateToUrl } from "single-spa";
import { useSessionStore } from "@/stores/session";
import { isTileRoutePermitted } from "@/nav/routeGuard";
import { opensWithNoCompany } from "@/nav/navScopes";
import { useOrganisationStore } from "@/stores/organisation";
import LogoTile from "@/shell/LogoTile.vue";
import type { AppHomeTile } from "@/app/types";
import { APP_BRANDING, APP_HOME_PAGE, APP_MODULES, APP_NAME } from "@/app";

/** Her resume, in the profile module (D164a) — where `/` sends a person with no staff company. */
export const MY_WORK_ROUTE = "/profile?section=productions";

/**
 * Rendered at `/` when `tenant.homePage?.enabled` (App.vue) — A7.
 * Tiles are pre-validated backend-side to sit under one of the app's module
 * prefixes (and inside `allowedItems` when set) — `isTileRoutePermitted`
 * re-checks that against the CALLER's actually-resolved modules, so a tile
 * configured before a module/allowedItems change was tightened never renders
 * a dead or now-forbidden link.
 */
export default defineComponent({
  name: "AppHome",
  components: { LogoTile },
  setup() {
    const session = useSessionStore();
    const organisation = useOrganisationStore();
    const { t } = useI18n();

    const tenantName = APP_NAME;
    const branding = APP_BRANDING;

    const welcomeTitle = APP_HOME_PAGE.welcomeTitle || APP_NAME;
    const welcomeText = APP_HOME_PAGE.welcomeText;

    /**
     * 🔴 App-scoped tiles ONLY (D32). This page is `/` — outside every production — and a
     * production-scoped tile here would open a screen with no production behind it: the "choose a
     * production" state a person is already in, dressed up as a destination. The way to a
     * production's budget is through the production, and that is the whole point of the stage.
     */
    const appTiles: AppHomeTile[] = APP_HOME_PAGE.tiles.filter(
      (tile) => tile.scope !== "production" && isTileRoutePermitted(tile.route, APP_MODULES),
    );
    /**
     * 🔴 In no company, the grid keeps what the rail keeps (D157, D164): once `GET /me/organisations` has
     * answered with nothing selectable — an actor whose only edges are agencies that represent her, however
     * many — Management, Admin and every company door would open onto refusals.
     */
    const visibleTiles = computed<AppHomeTile[]>(() =>
      organisation.ready && organisation.selectable.length === 0
        ? appTiles.filter((tile) => opensWithNoCompany(tile.route))
        : appTiles,
    );

    /**
     * 🔴 **A PERSON WITH NO STAFF COMPANY LANDS ON HER WORK** (D164a, docs/21 §1). Once `/me/organisations` has
     * answered with nothing selectable — an actor whose only edges are the agencies that represent her, however
     * many — this grid is a menu of her own two places, and her work IS the product. So `/` takes her there.
     */
    watch(
      () => organisation.ready && organisation.selectable.length === 0,
      (noCompany) => {
        if (noCompany) navigateToUrl(MY_WORK_ROUTE);
      },
      { immediate: true },
    );

    function onTileClick(tile: AppHomeTile): void {
      navigateToUrl(tile.route);
      const [path, query] = tile.route.split("?");
      window.dispatchEvent(
        new CustomEvent("pinksy:navigate", {
          detail: { path, query: Object.fromEntries(new URLSearchParams(query || "")) },
        }),
      );
    }

    /** First name only — a full name reads as a form field, not a greeting. */
    const greeting = computed(() => {
      const first = session.user?.displayName?.trim().split(/\s+/)[0];
      return first ? t("appHome.greeting", { name: first }) : null;
    });

    return { t, tenantName, branding, greeting, welcomeTitle, welcomeText, visibleTiles, onTileClick };
  },
});
</script>

<style scoped>
.app-home {
  max-width: 1040px;
  margin: 0 auto;
  padding: 28px 24px 40px;
}

.app-home__hero {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}
.app-home__greeting {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
  margin: 0 0 3px;
}
.app-home__title {
  font-size: 21px;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin: 0;
  color: var(--sh-text);
}
.app-home__subtitle {
  font-size: 13px;
  color: var(--sh-text-60);
  margin: 4px 0 0;
}

.app-home__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(228px, 1fr));
  gap: 12px;
}

/* Card language: hairline border on the card surface, a tinted icon tile,
   13px/700 title, 12px muted description. */
.app-home__tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--sh-hairline);
  background: rgb(var(--v-theme-card));
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition:
    transform 120ms ease,
    box-shadow 120ms ease,
    border-color 120ms ease;
}
.app-home__tile:hover,
.app-home__tile:focus-visible {
  border-color: rgba(var(--v-theme-primary), 0.55);
  box-shadow: 0 10px 24px -14px rgba(0, 0, 0, 0.55);
  transform: translateY(-1px);
  outline: none;
}
.app-home__tile-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  margin-bottom: 4px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
  color: #fff;
  box-shadow: 0 4px 12px -5px rgba(var(--v-theme-primary), 0.8);
}
.app-home__tile-icon :deep(.v-icon) {
  color: #fff !important;
}
.app-home__tile-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--sh-text);
}
.app-home__tile-desc {
  font-size: 12px;
  line-height: 1.45;
  color: var(--sh-text-60);
}
.app-home__tile-go {
  position: absolute;
  top: 16px;
  right: 14px;
  color: var(--sh-text-45) !important;
  opacity: 0;
  transition: opacity 120ms ease, transform 120ms ease;
}
.app-home__tile:hover .app-home__tile-go {
  opacity: 1;
  transform: translateX(2px);
}

.app-home__empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 48px 24px;
  border-radius: 12px;
  border: 1px dashed var(--sh-hairline);
}
.app-home__empty-icon {
  color: var(--sh-text-45) !important;
  margin-bottom: 4px;
}
.app-home__empty {
  font-size: 13px;
  font-weight: 600;
  color: var(--sh-text);
  margin: 0;
}
.app-home__empty-hint {
  font-size: 12px;
  color: var(--sh-text-45);
  margin: 0;
}
</style>
